/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxCallbacks,
    IBuyboxFindInStoreViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxProps,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxState,
    IBuyboxViewProps
} from '@msdyn365-commerce-modules/buybox';
import { ArrayExtensions, DeliveryMode } from '@msdyn365-commerce-modules/retail-actions';
import { IncrementalQuantity, ITelemetryContent, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { IHeiBuyboxData } from '../../../modules/hei-buybox';

import {
    IHeiBuyboxProps as IBuyboxExtentionProps,
    IHeiBuyboxResources as IBuyboxExtentionResources
} from '../definition-extensions/hei-buybox.ext.props.autogenerated';
import { getAttributeValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';
import { AuthorizedDealerBadge } from './components/AuthorizedDealerBadge';
import { MfrCode } from './components/MfrCode';
import { HideablePrice } from './components/HideablePrice';
import { ProductPromotions } from './components/ProductPromotions';
import AddThisShare from './components/add-this-share';
import { ProductWarranty } from './components/ProductWarranty';
import Bazzarvoice from '../../../modules/bazzarvoice-widget/components/Bazzarvoice';
import { widgetType } from '../../../modules/bazzarvoice-widget/bazzarvoice-widget.props.autogenerated';
import { JsonLinkingData } from './components/JsonLinkingData';
import { ProductBadge } from './components/ProductBadge';
import { useGoogleAnalytics } from '../../../common/useGoogleAnalytics';
import { findAttribute } from '../../../common/utilities/productListExtensionHelpers';
import { GaPayload } from '../../../common/pushGoogleAnalyticsEvent';

const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxExtentionProps<IHeiBuyboxData>> = props => {
    const {
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        telemetryContent,
        configure,
        description,
        findInStore,
        quantity,
        price,
        title,
        callbacks,
        state,
        resources,
        inventoryLabel,
        shopSimilarLook,
        keyInPrice,
        quantityLimitsMessages,
        max,
        shopSimilarDescription,
        unitOfMeasure
    } = props;

    const {
        data: { product, productPrice, categoryPaths }
    } = props;

    const { actionContext, request } = props.context;
    const [warrantySelected, setWarrantySelected] = React.useState(false);

    const recordId = props.context.actionContext.requestContext.urlTokens.recordId;
    const catalogId = props.context.actionContext.requestContext.apiSettings.catalogId;
    const channelId = props.context.actionContext.requestContext.apiSettings.channelId;

    // Master product attribute values
    const [attributeValues, setAttributeValues] = React.useState<AttributeValue[]>();

    React.useEffect(() => {
        (async () => {
            const attributes = await getAttributeValuesAsync({ callerContext: actionContext }, Number(recordId), channelId, catalogId);
            setAttributeValues(attributes);
        })();
    }, [recordId]);

    const productInfo = product.result;
    const productPriceData = productPrice.result;
    const itemId = productInfo?.ItemId;

    const hasSerialNumber = productInfo?.Behavior?.HasSerialNumber || false;
    const variantSku = productInfo?.ProductNumber ?? productInfo?.ItemId ?? '';

    const brand = React.useMemo(() => {
        const brandFromExt = productInfo?.ExtensionProperties?.find(p => p.Key === 'Brand')?.Value?.StringValue;
        if (brandFromExt) {
            return brandFromExt;
        }

        if (attributeValues) {
            const bandAttribute = findAttribute(attributeValues, 'Brand') || findAttribute(attributeValues, 'Marque');
            return bandAttribute?.TextValue || '';
        }

        return null;
    }, [productInfo, attributeValues]);

    const gaPayload = React.useMemo(() => {
        if (productInfo && productPriceData && variantSku) {
            const hasUnselectedOption = productInfo.Dimensions?.find(d => Boolean(!d.DimensionValue));

            if (hasUnselectedOption) {
                return undefined;
            }

            const paths = categoryPaths?.result?.map(p => p.Name) ?? [];
            const variant = productInfo.Dimensions?.map(d => d.DimensionValue?.Value).join(',');
            const discount = productPriceData.DiscountAmount ?? 0;
            const payload: GaPayload = {
                event: 'view_item',
                ecommerce: {
                    currency: productPriceData.CurrencyCode,
                    value: productPriceData.CustomerContextualPrice ?? 0,
                    items: [
                        {
                            item_id: variantSku,
                            item_name: productInfo.Name ?? '',
                            item_brand: brand ?? undefined,
                            item_variant: variant,
                            item_category: paths[0],
                            item_category2: paths[1],
                            item_category3: paths[2],
                            item_category4: paths[3],
                            item_category5: paths[4],
                            price: (productPriceData.BasePrice ?? 0) - discount,
                            discount: discount,
                            quantity: 1,
                            pid: productInfo.ItemId ?? '',
                            index: 0
                        }
                    ]
                }
            };

            return payload;
        }

        return undefined;
    }, [variantSku, brand]);

    useGoogleAnalytics(gaPayload);

    return (
        <Module {...ModuleProps}>
            <div className='buybox-title-container'>
                {title} <AuthorizedDealerBadge brand={brand} requestContext={request} />
            </div>
            <span>
                {resources.webCodeLabel}: {itemId ?? ''}
                <MfrCode mfrCodeLabel={resources.mfrCodeLabel} productInfo={productInfo} actionContext={actionContext} />
            </span>
            <div className='buybox-content'>
                {/* TODO: Improve media gallery performace */}
                <Node {...MediaGalleryContainerProps}>
                    <ProductBadge attributeValues={attributeValues} requestContext={request} productPrice={productPrice.result} />
                    {props.mediaGallery}
                </Node>
                <Node {...ProductInfoContainerProps}>
                    <HideablePrice attributeValues={attributeValues} price={price} seePriceInCartLabel={resources.seePriceInCartLabel} />
                    <Bazzarvoice itemId={itemId} widgetType={widgetType.rating_summary} className='hei-bazzarvoice-rating' />
                    {unitOfMeasure}
                    {/* {props.bulkPurchaseLink} */}
                    {description}
                    {_renderTextBlock(props.slots.textBlocks)}
                    {/* {rating} */}
                    <ProductPromotions
                        sitePath={request.sitePath ?? ''}
                        actionContext={actionContext}
                        recordId={productInfo?.MasterProductId}
                        variantId={productInfo?.RecordId}
                    />
                    {configure && _renderConfigure(configure)}
                    <ProductWarranty
                        context={props.context}
                        catalogId={catalogId}
                        extentionResources={resources}
                        productId={Number(recordId)}
                        setWarrantySelected={setWarrantySelected}
                    />
                    {keyInPrice && _renderKeyInPrice(keyInPrice)}
                    {quantity &&
                        !hasSerialNumber &&
                        _renderQuantity(
                            quantity,
                            callbacks,
                            props,
                            state,
                            resources,
                            quantityLimitsMessages,
                            max,
                            warrantySelected,
                            telemetryContent
                        )}
                    {inventoryLabel}
                    {_renderAvailability(props)}
                    {_renderCartAndActions(addToCart, addToOrderTemplate, addToWishlist)}
                    {findInStore && _renderFindInStore(findInStore)}
                    <AddThisShare />
                    {shopSimilarLook && _renderShopSimilarItem(shopSimilarLook)}
                    {shopSimilarDescription && _renderShopSimilarItem(shopSimilarDescription)}
                </Node>
            </div>
            <JsonLinkingData
                productInfo={productInfo}
                productPrice={productPrice.result}
                attributeValues={attributeValues}
                actionContext={actionContext}
            />
        </Module>
    );
};

const _renderTextBlock = (textBlocks: React.ReactNode[]): JSX.Element | undefined => {
    if (!textBlocks || textBlocks.length === 0) {
        return undefined;
    }

    return <>{textBlocks[0]}</>;
};

const _renderCartAndActions = (
    addToCart?: IBuyboxAddToCartViewProps,
    addToOrderTemplate?: IBuyboxAddToOrderTemplateViewProps,
    addToWishlist?: IBuyboxAddToWishlistViewProps
): JSX.Element | undefined => {
    if (!addToCart && !addToWishlist) {
        return undefined;
    }

    return (
        <div className='product-add-to-cart'>
            {addToCart && addToCart.errorBlock}
            {addToOrderTemplate && addToOrderTemplate.errorBlock}
            {addToWishlist && addToWishlist.errorBlock}
            <div className='buttons'>
                {addToCart && addToCart.button}
                {addToOrderTemplate && addToOrderTemplate.button}
                {addToWishlist && addToWishlist.button}
            </div>
        </div>
    );
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(_renderConfigureDropdown)}</Node>;
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {select}
        </Node>
    );
};

const _renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

/**
 * On Change function.
 * @param callbacks -Callbacks.
 * @returns Update quantity.
 */
const onChangeHandler = (callbacks: IBuyboxCallbacks) => (newValue: number): boolean => {
    if (callbacks.updateQuantity) {
        return callbacks.updateQuantity(newValue);
    }
    return true;
};

const _renderQuantity = (
    quantityComponent: IBuyboxProductQuantityViewProps,
    callbacks: IBuyboxCallbacks,
    props: IBuyboxProps<IHeiBuyboxData>,
    state: IBuyboxState,
    extentionResources: IBuyboxExtentionResources,
    quantityLimitsMessages: React.ReactNode,
    max: number | undefined,
    warrantyIsSelected: boolean,
    telemetryContent?: ITelemetryContent
): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;

    const { resources } = props;

    const { quantity } = state;

    if (warrantyIsSelected && quantity > 1) {
        callbacks.updateQuantity(1);
    }

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>

            <IncrementalQuantity
                id='ms-buybox__product-quantity-input'
                max={warrantyIsSelected ? 1 : max || 10}
                currentCount={quantity}
                onChange={onChangeHandler(callbacks)}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                decrementButtonAriaLabel={extentionResources.decrementButtonAriaLabel}
                incrementButtonAriaLabel={extentionResources.incrementButtonAriaLabel}
                minQuantityText={extentionResources.minQuantityText}
                maxQuantityText={extentionResources.maxQuantityText}
                telemetryContent={telemetryContent}
            />
            {quantityLimitsMessages}
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

const _renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

const _renderAvailability = (props: IBuyboxExtentionProps<IHeiBuyboxData>) => {
    const availDate = props.data.product.result?.ExtensionProperties?.find(ep => ep.Key === 'AvailDate')?.Value?.StringValue;
    const productAvailableQuantity = props.data.productAvailableQuantity?.result;

    process.env.NODE_ENV === 'development' && console.log('_renderAvailability', availDate);

    if (!availDate || !productAvailableQuantity || !ArrayExtensions.hasElements(productAvailableQuantity)) {
        return undefined;
    }

    const shippingInventory = productAvailableQuantity.find(shipping => shipping.deliveryType === DeliveryMode.shipping);

    if (shippingInventory) {
        process.env.NODE_ENV === 'development' && console.log('_renderAvailability', shippingInventory.StockLevelCode);
        if (shippingInventory.StockLevelCode == 'AVAIL') {
            return <p className='ms-buybox__find-in-store-description'>{`${props.resources.expectedAvailabilityLabel}: ${availDate}`}</p>;
        }
    }

    return undefined;
};

export default BuyboxView;
